// src/components/OurGallery.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import '../styles/OurGallery.css';

const OurGallery = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  
  const API_BASE_URL = 'https://eks-projectmanager.eksconstruction.in/api';
  const BASE_URL = 'https://eks-projectmanager.eksconstruction.in';

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/get_gallery.php`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Check if the response has the expected structure
      if (data.success && data.gallery) {
        setGalleryItems(data.gallery);
      } else if (Array.isArray(data)) {
        // If data is directly an array
        setGalleryItems(data);
      } else {
        throw new Error('Invalid data format received from server');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Gallery Error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.4 }
    }
  };

  const handleImageError = (e) => {
    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="Arial" font-size="16"%3EImage not found%3C/text%3E%3C/svg%3E';
  };

  if (loading) {
    return (
      <div className="gallery-container">
        <div className="gallery-loading">
          <motion.div 
            className="spinner"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p>Loading Gallery...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gallery-container">
        <div className="gallery-error">
          <h3>Error Loading Gallery</h3>
          <p>{error}</p>
          <button onClick={fetchGalleryItems} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-container">
      <motion.div 
        className="gallery-header"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2>Our Gallery</h2>
        <p>Explore our collection of construction projects and achievements</p>
      </motion.div>

      {galleryItems.length === 0 ? (
        <motion.div 
          className="gallery-no-items"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p>No gallery items available yet.</p>
        </motion.div>
      ) : (
        <motion.div 
          className="gallery-sections"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {galleryItems.map((item) => (
            <motion.div 
              key={item.id} 
              className="gallery-section"
              variants={itemVariants}
            >
              <div className="gallery-section-header">
                <h3>{item.heading}</h3>
              </div>

              <div className="gallery-images-grid">
                {item.images && item.images.length > 0 ? (
                  item.images.map((image, index) => (
                    <motion.div 
                      key={index}
                      className="gallery-image-wrapper"
                      variants={imageVariants}
                      whileHover={{ scale: 1.05 }}
                      onClick={() => {
                        setSelectedImageIndex(index);
                        setSelectedItemId(item.id);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <img 
                        src={`${BASE_URL}${image.url}`}
                        alt={`${item.heading} - ${index + 1}`}
                        loading="lazy"
                        className="gallery-image"
                        onError={handleImageError}
                      />
                      <div className="image-overlay">
                        <span className="zoom-icon">🔍</span>
                      </div>
                    </motion.div>
                  ))
                ) : (
                  <div className="no-images">
                    <p>No images available for this section</p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Modal for full-screen image view */}
      {selectedItemId !== null && selectedImageIndex !== null && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => {
            setSelectedImageIndex(null);
            setSelectedItemId(null);
          }}
        >
          <motion.div 
            className="modal-content"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="close-button"
              onClick={() => {
                setSelectedImageIndex(null);
                setSelectedItemId(null);
              }}
            >
              ✕
            </button>

            {(() => {
              const currentItem = galleryItems.find(item => item.id === selectedItemId);
              
              if (!currentItem || !currentItem.images || currentItem.images.length === 0) {
                return null;
              }

              return (
                <>
                  <div className="modal-image-container">
                    <motion.img 
                      key={selectedImageIndex}
                      src={`${BASE_URL}${currentItem.images[selectedImageIndex].url}`}
                      alt={`${currentItem.heading} - ${selectedImageIndex + 1}`}
                      className="modal-image"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      onError={handleImageError}
                    />
                  </div>

                  <div className="modal-navigation">
                    <button 
                      className="nav-button prev"
                      onClick={() => {
                        const totalImages = currentItem.images.length;
                        setSelectedImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
                      }}
                      disabled={currentItem.images.length <= 1}
                    >
                      ← Previous
                    </button>

                    <span className="image-counter">
                      {selectedImageIndex + 1} / {currentItem.images.length}
                    </span>

                    <button 
                      className="nav-button next"
                      onClick={() => {
                        const totalImages = currentItem.images.length;
                        setSelectedImageIndex((prev) => (prev + 1) % totalImages);
                      }}
                      disabled={currentItem.images.length <= 1}
                    >
                      Next →
                    </button>
                  </div>
                </>
              );
            })()}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default OurGallery;